import React from 'react';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';

// ── HELPERS ───────────────────────────────────────────────────────

const hasValue = (v) => v !== null && v !== undefined && v !== '';

const DataRow = ({ label, value, unit }) => {
  if (!hasValue(value)) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between',
      py: '3.5px', gap: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
        {unit && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

const MultilineValue = ({ value }) => {
  if (!hasValue(value)) return null;
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
      {value}
    </Typography>
  );
};

const GroupTitle = ({ label }) => (
  <Typography variant="caption" sx={{
    display: 'block',
    mb: 0.75, fontWeight: 700,
    letterSpacing: '0.09em', textTransform: 'uppercase',
    color: 'text.disabled',
    borderBottom: '1px solid', borderColor: 'divider',
    pb: '3px',
  }}>
    {label}
  </Typography>
);

/** Gruppo di campi in colonna, con bordo destro su schermi larghi */
const PanelGroup = ({ title, children }) => (
  <Box>
    <GroupTitle label={title} />
    {children}
  </Box>
);

/** Riga di gruppi affiancati in griglia responsiva */
const GroupRow = ({ children }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fill, minmax(140px, 1fr))' },
    gap: { xs: 1.5, sm: 2 },
  }}>
    {children}
  </Box>
);

/** Separatore tra sezioni macro con etichetta */
const SectionDivider = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
    <Typography variant="caption" sx={{
      fontWeight: 800, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'text.disabled',
      fontSize: 9, flexShrink: 0,
    }}>
      {label}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

// ── CHASSIS BOTTOM PANEL ──────────────────────────────────────────

/**
 * Pannello inferiore con tutte le sezioni non-per-angolo, visibili in verticale.
 * Nessuna tab: tutto sempre visibile.
 *
 * Props:
 *   setupData              – setupData raw
 *   fieldConfig            – output di buildFieldConfig()
 *   t                      – funzione di traduzione
 *   totalCalculatedWeight  – calcolato nel genitore
 *   calculatedBallastValue – stringa calcolata nel genitore
 */
const ChassisBottomPanel = ({ setupData, fieldConfig, t, totalCalculatedWeight, calculatedBallastValue }) => {
  const { isModuleActive } = fieldConfig;

  const ant       = setupData?.anteriore || {};
  const post      = setupData?.posteriore || {};
  const aero      = setupData?.aero || {};
  const diff      = setupData?.differenziale || {};
  const freni     = setupData?.freni || {};
  const el        = setupData?.elettronica || {};
  const weights   = setupData?.weights || {};
  const strategia = setupData?.strategia || {};
  const feedback  = setupData?.feedback || {};

  // Mostra la sezione se il modulo è attivo nel template OPPURE se ci sono dati storici
  const hasTelaio =
    isModuleActive('mechanical_grip') || isModuleActive('aero') || isModuleActive('electronics') ||
    hasValue(ant.barra) || hasValue(post.barra) || hasValue(diff.precarico) ||
    hasValue(freni.bilanciamentoFreni) || hasValue(aero.alaPosteriore) || hasValue(el.tc1);

  const hasPesi = isModuleActive('weights') ||
    hasValue(weights.dryWeight) || hasValue(weights.totalWeight) || hasValue(weights.cornerFL);

  const hasStrategia = isModuleActive('strategy_tires') ||
    hasValue(strategia.carburanteIniziale) || hasValue(strategia.strategiaPit);

  const hasFeedback = isModuleActive('feedback') ||
    hasValue(feedback.bestLap) || hasValue(feedback.noteTecnico) || hasValue(feedback.feedbackPilota);

  if (!hasTelaio && !hasPesi && !hasStrategia && !hasFeedback) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1.5 }}>

      {/* ═══ TELAIO ═══ */}
      {hasTelaio && (
        <>
          <SectionDivider label={t('cc_tab_chassis', { defaultValue: 'Telaio' })} />
          <GroupRow>
            {(hasValue(ant.barra) || hasValue(post.barra)) && (
              <PanelGroup title={t('cc_group_arb')}>
                <DataRow label={t('field_arb_front')} value={ant.barra} />
                <DataRow label={t('field_arb_rear')} value={post.barra} />
              </PanelGroup>
            )}
            {(hasValue(diff.precarico) || hasValue(diff.bloccaggio)) && (
              <PanelGroup title={t('subheader_diff', { defaultValue: 'Differenziale' })}>
                <DataRow label={t('field_diff_preload')} value={diff.precarico} unit="Nm" />
                <DataRow label={t('field_diff_lock')} value={diff.bloccaggio} unit="%" />
              </PanelGroup>
            )}
            {(hasValue(freni.bilanciamentoFreni) || hasValue(freni.potenzaFreni) || hasValue(freni.rapportoSterzo)) && (
              <PanelGroup title={t('cc_group_brakes')}>
                <DataRow label={t('field_brake_bias')} value={freni.bilanciamentoFreni} unit="%" />
                <DataRow label={t('field_brake_power')} value={freni.potenzaFreni} unit="%" />
                <DataRow label={t('field_steering_ratio')} value={freni.rapportoSterzo} />
              </PanelGroup>
            )}
            {(hasValue(aero.alaPosteriore) || hasValue(aero.splitterAnteriore) ||
              hasValue(aero.condottiFrenoAnteriori) || hasValue(aero.condottiFrenoPosteriori)) && (
              <PanelGroup title={t('section_aero', { defaultValue: 'Aerodinamica' })}>
                <DataRow label={t('field_wing_rear')} value={aero.alaPosteriore} />
                <DataRow label={t('field_splitter')} value={aero.splitterAnteriore} />
                <DataRow label={t('field_brake_ducts_front')} value={aero.condottiFrenoAnteriori} />
                <DataRow label={t('field_brake_ducts_rear')} value={aero.condottiFrenoPosteriori} />
              </PanelGroup>
            )}
            {(hasValue(el.tc1) || hasValue(el.tc2) || hasValue(el.abs) || hasValue(el.mappaECU)) && (
              <PanelGroup title={t('section_electronics', { defaultValue: 'Elettronica' })}>
                <DataRow label={t('field_electronics_tc1')} value={el.tc1} />
                <DataRow label={t('field_electronics_tc2')} value={el.tc2} />
                <DataRow label={t('field_electronics_abs')} value={el.abs} />
                <DataRow label={t('field_electronics_ecu')} value={el.mappaECU} />
              </PanelGroup>
            )}
          </GroupRow>
        </>
      )}

      {/* ═══ PESI ═══ */}
      {hasPesi && (
        <>
          <SectionDivider label={t('cc_tab_weights', { defaultValue: 'Pesi' })} />
          <GroupRow>
            {(hasValue(weights.dryWeight) || hasValue(weights.driverWeight) ||
              hasValue(weights.fuelLiters) || hasValue(weights.minRegWeight)) && (
              <PanelGroup title={t('subheader_weights_base')}>
                <DataRow label={t('detail_weights_dry')} value={weights.dryWeight} unit="kg" />
                <DataRow label={t('detail_weights_driver')} value={weights.driverWeight} unit="kg" />
                <DataRow label={t('detail_weights_fuel')} value={weights.fuelLiters} unit="L" />
                <DataRow label={t('detail_weights_min_reg')} value={weights.minRegWeight} unit="kg" />
              </PanelGroup>
            )}
            {(hasValue(weights.totalWeight) || hasValue(weights.totalFrontWeight) ||
              hasValue(weights.totalRearWeight) || hasValue(weights.weightRip)) && (
              <PanelGroup title={t('subheader_weights_total')}>
                <DataRow label={t('detail_weights_total_measured')} value={weights.totalWeight} unit="kg" />
                {totalCalculatedWeight > 0 && (
                  <DataRow label={t('detail_weights_total_calc')} value={totalCalculatedWeight.toFixed(1)} unit="kg" />
                )}
                <DataRow label={t('detail_weights_front_axle')} value={weights.totalFrontWeight} unit="kg" />
                <DataRow label={t('detail_weights_rear_axle')} value={weights.totalRearWeight} unit="kg" />
                <DataRow label={t('detail_weights_dist')} value={weights.weightRip} unit="%" />
              </PanelGroup>
            )}
            {(hasValue(weights.cornerFL) || hasValue(weights.cornerFR) ||
              hasValue(weights.cornerRL) || hasValue(weights.cornerRR)) && (
              <PanelGroup title={t('subheader_weights_corner')}>
                <DataRow label="FL" value={weights.cornerFL} unit="kg" />
                <DataRow label="FR" value={weights.cornerFR} unit="kg" />
                <DataRow label="RL" value={weights.cornerRL} unit="kg" />
                <DataRow label="RR" value={weights.cornerRR} unit="kg" />
              </PanelGroup>
            )}
            {(hasValue(weights.ballast) || (hasValue(calculatedBallastValue) && calculatedBallastValue !== 'N/A')) && (
              <PanelGroup title={t('subheader_weights_ballast')}>
                <DataRow label={t('field_ballast_applied')} value={weights.ballast} unit="kg" />
                {hasValue(calculatedBallastValue) && calculatedBallastValue !== 'N/A' && (
                  <DataRow label={t('field_ballast_calculated')} value={calculatedBallastValue} unit="kg" />
                )}
              </PanelGroup>
            )}
          </GroupRow>
        </>
      )}

      {/* ═══ STRATEGIA ═══ */}
      {hasStrategia && (
        <>
          <SectionDivider label={t('cc_tab_strategy', { defaultValue: 'Strategia' })} />
          <GroupRow>
            {(hasValue(strategia.carburanteIniziale) || hasValue(diff.fuelConsumption)) && (
              <PanelGroup title={t('subheader_strategy_fuel')}>
                <DataRow label={t('field_strategy_fuel_start')} value={strategia.carburanteIniziale} unit="L" />
                <DataRow label={t('field_fuel_consumption')} value={diff.fuelConsumption} unit="L/giro" />
              </PanelGroup>
            )}
            {(hasValue(freni.pastiglieFrenoAnteriori) || hasValue(freni.pastiglieFrenoPosteriori)) && (
              <PanelGroup title={t('subheader_strategy_brakes')}>
                <DataRow label={t('field_strategy_brakes_front')} value={freni.pastiglieFrenoAnteriori} />
                <DataRow label={t('field_strategy_brakes_rear')} value={freni.pastiglieFrenoPosteriori} />
              </PanelGroup>
            )}
            {hasValue(strategia.strategiaPit) && (
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <GroupTitle label={t('subheader_strategy_pit')} />
                <MultilineValue value={strategia.strategiaPit} />
              </Box>
            )}
          </GroupRow>
        </>
      )}

      {/* ═══ NOTE & FEEDBACK ═══ */}
      {hasFeedback && (
        <>
          <SectionDivider label={t('cc_tab_notes', { defaultValue: 'Note' })} />
          <GroupRow>
            {(hasValue(feedback.bestLap) || hasValue(feedback.listaTempi) ||
              hasValue(feedback.linkTelemetria) || hasValue(feedback.linkVideo)) && (
              <PanelGroup title={t('subheader_laps')}>
                <DataRow label={t('detail_feedback_best_lap')} value={feedback.bestLap} />
                {hasValue(feedback.listaTempi) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{t('detail_feedback_times')}</Typography>
                    <MultilineValue value={feedback.listaTempi} />
                  </Box>
                )}
                {hasValue(feedback.linkTelemetria) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detail_feedback_telemetry')}
                    </Typography>
                    <Link href={feedback.linkTelemetria} target="_blank" rel="noopener noreferrer" variant="caption" noWrap display="block">
                      {feedback.descrizioneTelemetria || feedback.linkTelemetria}
                    </Link>
                  </Box>
                )}
                {hasValue(feedback.linkVideo) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detail_feedback_video')}
                    </Typography>
                    <Link href={feedback.linkVideo} target="_blank" rel="noopener noreferrer" variant="caption" noWrap display="block">
                      {feedback.descrizioneVideo || feedback.linkVideo}
                    </Link>
                  </Box>
                )}
              </PanelGroup>
            )}
            {hasValue(feedback.noteTecnico) && (
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <GroupTitle label={t('detail_feedback_tech_notes')} />
                <MultilineValue value={feedback.noteTecnico} />
              </Box>
            )}
            {hasValue(feedback.feedbackPilota) && (
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <GroupTitle label={t('detail_feedback_driver')} />
                <MultilineValue value={feedback.feedbackPilota} />
              </Box>
            )}
          </GroupRow>
        </>
      )}

    </Paper>
  );
};

export default ChassisBottomPanel;
